import { AlertSeverity } from './detect';

interface Tone {
  frequency: number;
  beeps: number;
}

/** Rising pitch and repeat count with severity, so urgency is audible. */
const TONES: Record<AlertSeverity, Tone> = {
  info: { frequency: 620, beeps: 1 },
  warn: { frequency: 820, beeps: 2 },
  urgent: { frequency: 1040, beeps: 3 },
};

const BEEP_MS = 110;
const GAP_MS = 70;
/** Well below full scale: this plays on a pit wall, not through headphones. */
const PEAK_GAIN = 0.12;

type AudioContextCtor = typeof AudioContext;

function audioContextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

export interface AlertSound {
  /**
   * Browsers refuse to start audio without a gesture, so this is called from the
   * click that turns sound on — otherwise the first real alert would be silent.
   */
  unlock(): void;
  play(severity: AlertSeverity): void;
  close(): void;
}

/**
 * Short synthesised beeps.
 *
 * Synthesised rather than loaded from a file so there is no asset to ship and
 * nothing to fetch mid-race. Every entry point is a no-op where Web Audio is
 * unavailable (jsdom under test, older browsers), so callers need no guards.
 */
export function createAlertSound(): AlertSound {
  let ctx: AudioContext | null = null;

  const ensure = (): AudioContext | null => {
    if (ctx) return ctx;
    const Ctor = audioContextCtor();
    if (!Ctor) return null;
    try {
      ctx = new Ctor();
    } catch {
      return null;
    }
    return ctx;
  };

  const beep = (context: AudioContext, frequency: number, startAt: number): void => {
    const oscillator = context.createOscillator();
    const gain = context.createGain();

    oscillator.type = 'sine';
    oscillator.frequency.value = frequency;

    // Ramped rather than switched, because an abrupt start or stop clicks.
    gain.gain.setValueAtTime(0.0001, startAt);
    gain.gain.exponentialRampToValueAtTime(PEAK_GAIN, startAt + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + BEEP_MS / 1000);

    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start(startAt);
    oscillator.stop(startAt + BEEP_MS / 1000 + 0.02);
  };

  return {
    unlock(): void {
      const context = ensure();
      if (context && context.state === 'suspended') void context.resume();
    },

    play(severity: AlertSeverity): void {
      const context = ensure();
      if (!context) return;
      if (context.state === 'suspended') void context.resume();

      const tone = TONES[severity];
      for (let i = 0; i < tone.beeps; i += 1) {
        beep(
          context,
          tone.frequency,
          context.currentTime + (i * (BEEP_MS + GAP_MS)) / 1000,
        );
      }
    },

    close(): void {
      if (!ctx) return;
      void ctx.close();
      ctx = null;
    },
  };
}
